import { Card, DataList, Text, Theme } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { ResultBadge } from 'components/sections/analytics/session-hits/recap/result-badge';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { t } from 'i18next';
import { IPointer } from 'interfaces/session-recap/i-pointer';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { isNil } from 'lodash';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VALUE_FALLBACK = '-';

export const formatRecapValue = (
  v: number | null | undefined,
  decimals: number,
  unit?: string,
  transform?: (v: number) => number
) => {
  if (isNil(v) || isNaN(v)) {
    return VALUE_FALLBACK;
  }

  const value = (transform ? transform(v) : v).toFixed(decimals);
  return unit ? `${value} ${unit}` : value;
};

const CURSOR_GAP_PX = 14;
const VIEWPORT_PAD_PX = 8;

interface IProps {
  record: ISessionRecapHit;
  /** where the cursor entered the dot; the card follows the cursor from there */
  pointer: IPointer;
}

/** portalled so it never takes part in the page layout; Theme keeps the radix tokens */
export const ChartTooltip = ({ record, pointer }: IProps) => {
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const ref = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<IPointer>(pointer);
  const [position, setPosition] = useState<IPointer>();

  useEffect(() => {
    const onMove = (e: MouseEvent) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    const { offsetWidth: w, offsetHeight: h } = el;

    const right = cursor.x + CURSOR_GAP_PX;
    const x =
      right + w > window.innerWidth - VIEWPORT_PAD_PX
        ? cursor.x - CURSOR_GAP_PX - w
        : right;

    const y = Math.max(
      VIEWPORT_PAD_PX,
      Math.min(
        cursor.y - CURSOR_GAP_PX,
        window.innerHeight - VIEWPORT_PAD_PX - h
      )
    );

    setPosition({ x, y });
  }, [cursor, record]);

  const { pitch, hit } = record.hit;
  const hasContact = record.isContact;

  const isMetric = sessionMachine?.isMetric;
  const speedUnit = MetricHelper.getSpeedUnit(isMetric);
  const speedFn = isMetric ? MetricHelper.mphToKph : undefined;
  const breakUnit = MetricHelper.getBreaksUnit(isMetric);
  const breakFn = isMetric ? MetricHelper.inToCm : undefined;

  // same reading order as the pitch card: type, speed, breaks, then batted ball
  const rows: { label: string; value: ReactNode }[] = [
    {
      label: 'common.pitch-type',
      value: pitch?.type ?? VALUE_FALLBACK,
    },
    {
      label: 'hitters.recap-pitch-speed',
      value: formatRecapValue(pitch?.speedMPH, 1, speedUnit, speedFn),
    },
    {
      label: 'common.v-break',
      value: formatRecapValue(pitch?.zBreakIN, 1, breakUnit, breakFn),
    },
    {
      label: 'common.h-break',
      value: formatRecapValue(
        pitch?.xBreakIN === undefined ? undefined : -pitch.xBreakIN,
        1,
        breakUnit,
        breakFn
      ),
    },
    {
      label: 'common.exit-velocity',
      value: hasContact
        ? formatRecapValue(hit?.exitMPH, 1, speedUnit, speedFn)
        : VALUE_FALLBACK,
    },
    {
      label: 'common.launch-angle',
      value: hasContact
        ? formatRecapValue(hit?.vLaunchDEG, 0, '°')
        : VALUE_FALLBACK,
    },
    {
      label: 'common.distance',
      value: hasContact
        ? formatRecapValue(
            hit?.distanceFT,
            0,
            MetricHelper.getDistanceUnit(isMetric),
            isMetric ? MetricHelper.ftToM : undefined
          )
        : VALUE_FALLBACK,
    },
    {
      label: 'common.result',
      value: <ResultBadge result={record.result} size="1" />,
    },
  ];

  return createPortal(
    <Theme asChild>
      <div
        ref={ref}
        className="recap-chart-tooltip"
        style={{
          left: position?.x ?? 0,
          top: position?.y ?? 0,
          visibility: position ? 'visible' : 'hidden',
        }}
      >
        <Card size="1">
          <Text size="2" weight="bold">
            {t('hitters.recap-pitch-x', { x: record.pitchNumber })}
          </Text>

          <DataList.Root size="1" mt="1" trim="end">
            {rows.map((r) => (
              <DataList.Item key={r.label}>
                <DataList.Label minWidth="96px">{t(r.label)}</DataList.Label>
                <DataList.Value>{r.value}</DataList.Value>
              </DataList.Item>
            ))}
          </DataList.Root>
        </Card>
      </div>
    </Theme>,
    document.body
  );
};
